import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  Bell, X, Instagram, Linkedin, Hash, Image, 
  Calendar, Clock, Check, ChevronRight, Send
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import '../styles/AdminNotifications.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORMS = {
  instagram: { icon: Instagram, color: '#E1306C', label: 'Instagram' },
  linkedin: { icon: Linkedin, color: '#0077B5', label: 'LinkedIn' },
  tiktok: { icon: Hash, color: '#000000', label: 'TikTok' },
  all: { icon: Image, color: '#8B5CF6', label: 'All Platforms' }
};

const AdminNotifications = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState({ today: [], tomorrow: [] });
  const [dismissedIds, setDismissedIds] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/admin/content-schedule/upcoming/reminders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch on mount and every 5 minutes
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Load dismissed IDs from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('cadence_dismissed_notifications');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Only keep dismissals from today
        const today = new Date().toDateString();
        if (parsed.date === today) {
          setDismissedIds(parsed.ids || []);
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
  }, []);

  const dismissNotification = (id) => {
    const newDismissed = [...dismissedIds, id];
    setDismissedIds(newDismissed);
    localStorage.setItem('cadence_dismissed_notifications', JSON.stringify({
      date: new Date().toDateString(),
      ids: newDismissed
    }));
  };

  // Send a test push notification to the current admin's device(s)
  const [testPushState, setTestPushState] = useState('idle'); // idle | sending | sent | nosub | error

  const sendTestPush = async () => {
    if (!token) return;
    setTestPushState('sending');
    try {
      const res = await fetch(`${API_URL}/api/push/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        setTestPushState('error');
      } else {
        const data = await res.json();
        setTestPushState(data.status === 'sent' ? 'sent' : 'nosub');
      }
    } catch (err) {
      console.error('Test push failed:', err);
      setTestPushState('error');
    } finally {
      setTimeout(() => setTestPushState('idle'), 3500);
    }
  };

  const testPushLabel = {
    idle: 'Send test push to me',
    sending: 'Sending...',
    sent: 'Test push sent — check your device',
    nosub: 'No subscription on this device',
    error: 'Could not send — try again'
  }[testPushState];

  const markAsPosted = async (postId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/content-schedule/${postId}/mark-posted`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchNotifications();
      }
    } catch (err) {
      console.error('Mark posted error:', err);
    }
  };

  // Filter out dismissed notifications
  const activeToday = notifications.today.filter(n => !dismissedIds.includes(n.id));
  const activeTomorrow = notifications.tomorrow.filter(n => !dismissedIds.includes(n.id));
  const totalCount = activeToday.length + activeTomorrow.length;

  return (
    <div className="admin-notifications-container">
      <button 
        className={`notification-bell content-reminders ${totalCount > 0 ? 'has-notifications' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        data-testid="content-reminders-bell"
        title="Content Reminders"
      >
        <Calendar size={20} />
        {totalCount > 0 && (
          <span className="notification-badge">{totalCount}</span>
        )}
      </button>

      {isOpen && (
        <>
          <div className="notification-backdrop" onClick={() => setIsOpen(false)} />
          <div className="notification-dropdown" data-testid="notification-dropdown">
            <div className="notification-header">
              <h4>Content Reminders</h4>
              <button onClick={() => setIsOpen(false)} className="close-btn">
                <X size={18} />
              </button>
            </div>

            <div className="notification-body">
              {loading ? (
                <div className="notification-loading">Loading...</div>
              ) : totalCount === 0 ? (
                <div className="notification-empty">
                  <Bell size={32} />
                  <p>No upcoming posts</p>
                  <span>You&apos;re all caught up!</span>
                </div>
              ) : (
                <>
                  {/* Today's Posts */}
                  {activeToday.length > 0 && (
                    <div className="notification-section">
                      <div className="section-header today">
                        <Calendar size={14} />
                        <span>Due Today</span>
                        <span className="count">{activeToday.length}</span>
                      </div>
                      {activeToday.map(post => (
                        <NotificationItem 
                          key={post.id}
                          post={post}
                          isToday={true}
                          onDismiss={() => dismissNotification(post.id)}
                          onMarkPosted={() => markAsPosted(post.id)}
                          onViewCalendar={() => {
                            setIsOpen(false);
                            navigate('/admin/calendar');
                          }}
                        />
                      ))}
                    </div>
                  )}

                  {/* Tomorrow's Posts */}
                  {activeTomorrow.length > 0 && (
                    <div className="notification-section">
                      <div className="section-header tomorrow">
                        <Clock size={14} />
                        <span>Due Tomorrow</span>
                        <span className="count">{activeTomorrow.length}</span>
                      </div>
                      {activeTomorrow.map(post => (
                        <NotificationItem 
                          key={post.id}
                          post={post}
                          isToday={false}
                          onDismiss={() => dismissNotification(post.id)}
                          onMarkPosted={() => markAsPosted(post.id)}
                          onViewCalendar={() => {
                            setIsOpen(false);
                            navigate('/admin/calendar');
                          }}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="notification-footer">
              <button 
                className="view-calendar-btn"
                onClick={() => {
                  setIsOpen(false);
                  navigate('/admin/calendar');
                }}
              >
                View Content Calendar
                <ChevronRight size={16} />
              </button>
              <button
                className={`test-push-btn test-push-${testPushState}`}
                onClick={sendTestPush}
                disabled={testPushState === 'sending'}
                data-testid="send-test-push-btn"
                title="Fires a real push notification to verify your device subscription"
              >
                <Send size={14} />
                <span>{testPushLabel}</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const NotificationItem = ({ post, isToday, onDismiss, onMarkPosted, onViewCalendar }) => {
  const platform = PLATFORMS[post.platform] || PLATFORMS.all;
  const PlatformIcon = platform.icon;

  return (
    <div className={`notification-item ${isToday ? 'urgent' : ''}`}>
      <div className="notification-platform" style={{ backgroundColor: platform.color }}>
        <PlatformIcon size={14} />
      </div>
      <div className="notification-content">
        <span className="notification-title">{post.title}</span>
        <span className="notification-meta">
          {platform.label} • {post.post_type}
          {post.scheduled_time && ` • ${post.scheduled_time}`}
        </span>
      </div>
      <div className="notification-actions">
        {isToday && (
          <button 
            className="mark-posted-btn"
            onClick={(e) => { e.stopPropagation(); onMarkPosted(); }}
            title="Mark as Posted"
          >
            <Check size={14} />
          </button>
        )}
        <button 
          className="dismiss-btn"
          onClick={(e) => { e.stopPropagation(); onDismiss(); }}
          title="Dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

export default AdminNotifications;
